// 只规范化剧本展示分类，不接触玩家实际alignment，也不改原始JSON。
const roleKey = (id) =>
  String(id == null ? "" : id)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
const labels = Object.freeze({
  townsfolk: "镇民",
  outsider: "外来者",
  minion: "爪牙",
  demon: "恶魔",
  traveler: "旅行者",
  fabled: "传奇角色",
  jinxed: "相克",
});
const ownText = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key) &&
  typeof object[key] === "string"
    ? object[key].trim()
    : "";
const metaText = (object, key) =>
  ownText(object, key) ||
  ownText(
    object,
    Object.keys(object || {}).find(
      (candidate) =>
        candidate.trim().toLowerCase() === key.trim().toLowerCase(),
    ),
  );
const isJinxTeam = (team) =>
  /^(?:[a-z0-9]+[\s_-]+)?(?:jinxed|jinxes|jinx|hatred|hate)$/i.test(
    String(team || "").trim(),
  );
const findKnown = (known, id) =>
  known.get(roleKey(id)) ||
  [...known.values()].find((role) => roleKey(role.id) === roleKey(id));
// 兼容导出器的资源后缀，不改写角色ID、技能或Token。只有基名、名称和
// 分类同时指向唯一内置角色才建立别名；自制 mybutton / 同名异分类不猜。
function roleIdentity(role, known = new Map()) {
  if (!role || typeof role !== "object") return "";
  const id = String(role.id || "");
  const key = roleKey(id);
  const base = key.replace(/(?:button|buttion)$/, "");
  if (base === key) return id;
  const matches = [...known.values()].filter(item => roleKey(item.id) === base &&
    item.name === role.name && item.team === role.team);
  return matches.length === 1 ? matches[0].id : id;
}
// 两个上传入口共用的身份预检。必须在任何store写入之前执行，防止Map
// 静默覆盖 foo_bar / foobar，或未知引用被后续filter静默丢弃。
function scriptIdentityErrors(input, known = new Map(), { allowPartialReferences = false } = {}) {
  if (!Array.isArray(input) || !input.length) return ["剧本必须是非空JSON数组"];
  const errors = [], seen = new Map();
  input.forEach((value, index) => {
    const role = typeof value === "string" ? { id: value } : value;
    const at = `JSON[${index}]（第${index + 1}项）`;
    if (!role || typeof role !== "object" || Array.isArray(role)) {
      errors.push(`${at}不是角色对象或ID`); return;
    }
    if (role.id === "_meta") return;
    const id = roleKey(role.id);
    if (!id || id.length > 64) errors.push(`${at}.id缺少合法ID或规范化后超过64字符`);
    else if (seen.has(id)) errors.push(`${at}.id「${role.id}」与JSON[${seen.get(id)}]规范化后冲突：${id}`);
    else seen.set(id, index);
    const matches = [...known.values()].filter(item => roleKey(item.id) === id);
    if (matches.length > 1) errors.push(`${at}.id「${role.id}」在角色库中不唯一`);
    if (!allowPartialReferences && !matches.length && !(role.name && role.ability && role.team))
      errors.push(`${at}角色「${role.id || "未知"}」没有已知定义，须提供name、ability、team；不会自动猜测角色`);
  });
  return errors;
}
function normalizeScriptRole(raw, known = new Map(), legendaryIds = new Set()) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  const sourceTeam = String(
      Object.prototype.hasOwnProperty.call(raw, "sourceTeam")
        ? raw.sourceTeam
        : raw.team || "",
    ),
    team = String(raw.team || "")
      .trim()
      .toLowerCase();
  const legendary = (role) =>
    role && (role.team === "fabled" || legendaryIds.has(roleKey(role.id)));
  let normalized = team,
    reason = "standard";
  if (team === "traveller") {
    normalized = "traveler";
    reason = "traveller-alias";
  } else if (isJinxTeam(team)) {
    normalized = "jinxed";
    reason = "jinx-group";
  } else if (team === "encounter") {
    const exact = findKnown(known, raw.id),
      key = roleKey(raw.id);
    // 仅兼容CustomVER旧ID约定，且必须匹配到明确的已知传奇定义。
    const base = /customver$/.test(key)
      ? findKnown(known, key.replace(/customver$/, ""))
      : null;
    if (legendary(exact)) {
      normalized = "fabled";
      reason = "known-fabled-id";
    } else if (legendary(base)) {
      normalized = "fabled";
      reason = "known-fabled-customver-id";
    } else reason = "unknown-encounter";
  } else if (!team) {
    const role = findKnown(known, raw.id);
    if (role?.team) {
      normalized = role.team;
      reason = "known-role-missing-team";
    } else {
      normalized = "unknown";
      reason = "missing-team";
    }
  } else if (team === "loric" && legendary(findKnown(known, raw.id))) {
    normalized = "fabled";
    reason = "known-legendary-catalog";
  } else if (!Object.prototype.hasOwnProperty.call(labels, team))
    reason = "custom-group";
  if (
    Object.prototype.hasOwnProperty.call(raw, "sourceTeam") &&
    raw.team === normalized &&
    typeof raw.classificationReason === "string"
  )
    reason = raw.classificationReason;
  const relations = [
    ...new Map(
      ["jinxes", "jinxed", "jinx", "hatred", "hate"]
        .flatMap((key) => (Array.isArray(raw[key]) ? raw[key] : []))
        .map((item) => [JSON.stringify(item), item]),
    ).values(),
  ];
  return {
    ...raw,
    team: normalized,
    sourceTeam,
    sourceId: raw.sourceId || String(raw.id || ""),
    classificationReason: reason,
    jinxes: relations,
  };
}
function groupLabel(team, meta = {}) {
  return (
    metaText(meta.groupNames, team) ||
    metaText(meta, team + "Name") ||
    metaText(meta, team) ||
    (Object.prototype.hasOwnProperty.call(labels, team) ? labels[team] : "") ||
    ([...team].some((char) => char.charCodeAt(0) > 127) ? team : "未识别分类")
  );
}
function groupNames(meta, roles) {
  const names = Object.create(null);
  for (const role of roles || []) {
    const team = String(role?.sourceTeam || role?.team || ""),
      value =
        ownText(meta.groupNames, team) ||
        ownText(meta, team + "Name") ||
        ownText(meta, team);
    if (team && value) names[team] = value;
  }
  return names;
}
function classificationDiagnostics(roles, meta = {}) {
  const diagnostics = [],
    seen = new Set();
  for (const role of roles || []) {
    if (!role || typeof role !== "object") continue;
    const id = roleKey(role.id);
    if (seen.has(id))
      diagnostics.push({
        code: "DUPLICATE_ID",
        id: role.sourceId || role.id,
        message: `角色ID重复：${
          role.sourceId || role.id
        }；沿用现有同ID最后定义，不创建重复角色。`,
      });
    seen.add(id);
    if (
      ["unknown-encounter", "missing-team"].includes(role.classificationReason)
    )
      diagnostics.push({
        code: role.classificationReason,
        id: role.sourceId || role.id,
        sourceTeam: role.sourceTeam,
        message: `角色「${role.name || role.id}」分类未确认（${
          role.sourceTeam || "缺少team"
        }），已保留数据，未自动转为传奇角色。`,
      });
    else if (
      role.classificationReason === "custom-group" &&
      groupLabel(role.team, meta) === "未识别分类"
    )
      diagnostics.push({
        code: "UNKNOWN_GROUP",
        id: role.id,
        sourceTeam: role.sourceTeam,
        message: `分组「${role.sourceTeam}」没有显示名称，已保留角色；可在_meta中补充分组名称。`,
      });
  }
  return diagnostics;
}
function referenceGroups(roles, meta = {}) {
  const groups = Object.create(null),
    names = Object.create(null),
    merged = new Map();
  for (const role of roles.values()) {
    if (role.team === "fabled" || isJinxTeam(role.team)) continue;
    const label = groupLabel(role.team, meta),
      standard = Object.prototype.hasOwnProperty.call(labels, role.team),
      key = standard ? role.team : merged.get(label) || role.team;
    if (!standard) merged.set(label, key);
    if (!groups[key]) {
      groups[key] = [];
      names[key] = label;
    }
    groups[key].push(role);
  }
  return { groups, names };
}
function collectJinxes(roles, fabled = [], builtin = new Map()) {
  const all = new Map(),
    result = [],
    seen = new Map();
  [...roles.values(), ...fabled].forEach((role) => {
    if (role?.id) all.set(roleKey(role.id), role);
  });
  const add = (firstId, secondId, reason, entry, directed = false) => {
    if (typeof reason !== "string" || !reason.trim()) return;
    const a = roleKey(firstId),
      b = roleKey(secondId),
      first = all.get(a),
      second = all.get(b),
      text = reason.trim().replace(/\r\n/g, "\n");
    const pair = directed ? [a, b] : [a || roleKey(entry?.id), b].sort(),
      key = JSON.stringify([directed, ...pair, text]);
    const item = {
      first,
      second,
      reason: text,
      name:
        entry?.name ||
        [first?.name || firstId, second?.name || secondId].join("＆"),
      entry,
    };
    if (seen.has(key)) {
      // 作者的独立相克条目优先决定显示顺序，仍只展示同一条规则。
      if (entry) Object.assign(seen.get(key), item);
      return;
    }
    seen.set(key, item);
    result.push(item);
  };
  all.forEach((role, id) => {
    if (isJinxTeam(role.team)) {
      const pair = String(role.sourceId || role.id).match(/^jinx:(.+?)&(.+)$/i);
      add(pair?.[1], pair?.[2], role.ability, role, role.directed === true);
      return;
    }
    const known = builtin.get(id);
    if (known instanceof Map)
      known.forEach((reason, second) => {
        if (all.has(roleKey(second))) add(id, second, reason);
      });
    for (const key of ["jinxes", "jinxed", "jinx", "hatred", "hate"]) {
      if (Array.isArray(role[key]))
        role[key].forEach((item) => {
          if (item && typeof item === "object")
            add(id, item.id, item.reason, undefined, item.directed === true);
        });
    }
  });
  return result;
}
module.exports = {
  roleKey,
  roleIdentity,
  scriptIdentityErrors,
  labels,
  isJinxTeam,
  normalizeScriptRole,
  groupLabel,
  groupNames,
  classificationDiagnostics,
  referenceGroups,
  collectJinxes,
};
